import { type FormEvent, type JSX, useEffect, useRef, useState } from 'react';

import { buchungsliste, type Buchungstreffer } from '../api/buchungen';
import { ApiFehler } from '../api/fehler';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import { Meldung } from '../komponenten/Meldung';
import { BUCHUNGSFILTER, gesetzteFilter, type Buchungsfilter } from '../wegweiser';
import { STANDNAME, STATUSWAHL, zeitpunkt } from './Buchungssuche';

/**
 * DIE BUCHUNGEN ALS LISTE — DER EINSTIEG DER MASKE (Auftrag CRM-70).
 *
 * Ulf am 06.10.2026: „in dieser Ansicht sollen die Buchungen angezeigt
 * werden, mit optionalen zum Filter." Bis dahin war `/buchungen` ohne Nummer
 * leer.
 *
 * DIE FILTER STEHEN IN DER ADRESSE und werden auf Knopfdruck angewandt, wie in
 * der Buchungssuche: Ein Name, der nach jedem Buchstaben sucht, zeigt
 * Zwischenstände, und unter drei Zeichen weist der Server ihn ab (KUN-22).
 * Reihenfolge und die Untergrenze „ab heute" legt der Server fest.
 *
 * ZURÜCK AUF DIE ZEILE: Die Maske wird beim Öffnen einer Buchung neu
 * aufgebaut (eine andere Seite, `schluesselVon`). Welche Buchung zuletzt aus
 * der Liste geöffnet wurde, merkt sich deshalb das Modul und nicht die
 * Komponente — beim Zurück bekommt ihre Zeile den Fokus (WCAG 2.4.3).
 */
let zuletztGeoeffnet: string | null = null;

type Entwurf = Record<(typeof BUCHUNGSFILTER)[number], string>;

function alsEntwurf(filter: Buchungsfilter): Entwurf {
  return {
    von: filter.von ?? '',
    bis: filter.bis ?? '',
    status: filter.status ?? '',
    produkt: filter.produkt ?? '',
    name: filter.name ?? '',
  };
}

export function Buchungsliste({
  filter,
  beiFilter,
  beiOeffnen,
}: {
  /** Die angewandten Filter, aus der Adresse. */
  readonly filter: Buchungsfilter;
  readonly beiFilter: (filter: Buchungsfilter) => void;
  readonly beiOeffnen: (buchungsnummer: string) => void;
}): JSX.Element {
  const gesetzt = gesetzteFilter(filter);
  /* Als Text: Das Objekt entsteht bei jedem Bild neu. */
  const kennung = new URLSearchParams(gesetzt).toString();

  const [entwurf, setzeEntwurf] = useState<Entwurf>(() => alsEntwurf(gesetzt));
  const [zeilen, setzeZeilen] = useState<readonly Buchungstreffer[] | null>(null);
  const [mehr, setzeMehr] = useState(false);
  const [abHeute, setzeAbHeute] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const tabelle = useRef<HTMLTableElement>(null);

  /* Zurück und Vor im Browser ändern die Filter von außen — das Formular folgt. */
  useEffect(() => {
    setzeEntwurf(alsEntwurf(gesetzteFilter(Object.fromEntries(new URLSearchParams(kennung)))));
  }, [kennung]);

  useEffect(() => {
    let abgebrochen = false;
    setzeLaeuft(true);
    setzeFehler(null);
    void (async () => {
      try {
        const seite = await buchungsliste(gesetzteFilter(Object.fromEntries(new URLSearchParams(kennung))));
        if (abgebrochen) return;
        setzeZeilen(seite.buchungen);
        setzeMehr(seite.mehr);
        setzeAbHeute(seite.abHeute);
      } catch (f) {
        if (abgebrochen) return;
        setzeZeilen(null);
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        if (!abgebrochen) setzeLaeuft(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [kennung]);

  /* Die Zeile, aus der man in die Buchung ging, bekommt beim Zurück den Fokus. */
  useEffect(() => {
    const ziel = zuletztGeoeffnet;
    if (zeilen === null || ziel === null) return;
    zuletztGeoeffnet = null;
    Array.from(tabelle.current?.querySelectorAll('a') ?? [])
      .find((a) => a.textContent === ziel)
      ?.focus();
  }, [zeilen]);

  const weitere = async (): Promise<void> => {
    if (zeilen === null) return;
    setzeLaeuft(true);
    setzeFehler(null);
    try {
      const seite = await buchungsliste(gesetzt, zeilen.length);
      setzeZeilen([...zeilen, ...seite.buchungen]);
      setzeMehr(seite.mehr);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  };

  const anwenden = (ereignis: FormEvent): void => {
    ereignis.preventDefault();
    beiFilter(gesetzteFilter(entwurf));
  };

  const setze = (schluessel: keyof Entwurf, wert: string): void => {
    setzeEntwurf((alt) => ({ ...alt, [schluessel]: wert }));
  };

  const gefiltert = kennung !== '';

  return (
    <section className="karte" aria-labelledby="buchungsliste-titel">
      <h2 id="buchungsliste-titel">Alle Buchungen</h2>
      <form onSubmit={anwenden} aria-label="Buchungen filtern">
        <div className="suchmaske__felder">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bl-von">
              Zeitraum von
            </label>
            <input
              id="bl-von"
              type="date"
              className="feld__eingabe"
              value={entwurf.von}
              onChange={(e) => setze('von', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bl-bis">
              Zeitraum bis
            </label>
            <input
              id="bl-bis"
              type="date"
              className="feld__eingabe"
              value={entwurf.bis}
              onChange={(e) => setze('bis', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bl-status">
              Stand
            </label>
            <select
              id="bl-status"
              className="feld__eingabe"
              value={entwurf.status}
              onChange={(e) => setze('status', e.target.value)}
            >
              {STATUSWAHL.map((s) => (
                <option key={s.wert} value={s.wert}>
                  {s.text}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bl-produkt">
              Training
            </label>
            <input
              id="bl-produkt"
              className="feld__eingabe"
              value={entwurf.produkt}
              placeholder="Bezeichnung oder Artikelnummer"
              onChange={(e) => setze('produkt', e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="bl-name">
              Name (Kunde oder Teilnehmer)
            </label>
            <input
              id="bl-name"
              className="feld__eingabe"
              value={entwurf.name}
              onChange={(e) => setze('name', e.target.value)}
            />
          </div>
        </div>
        <p className="feld__hilfe">
          Alle Filter sind wahlfrei. Training und Name ab drei Zeichen, unscharf.
        </p>
        <div className="knopfreihe">
          <button type="submit" className="knopf" disabled={laeuft}>
            Filtern
          </button>
          {gefiltert && (
            <button type="button" className="knopf knopf--verweis" onClick={() => beiFilter({})}>
              Filter zurücksetzen
            </button>
          )}
        </div>
      </form>

      {fehler !== null && <Meldung fehler={fehler} />}

      {zeilen !== null && (
        <p className="feld__hilfe" aria-live="polite">
          {abHeute ? 'Termine ab heute, der nächste zuerst' : 'Termine im gewählten Zeitraum, der früheste zuerst'}
          {' · '}
          {zeilen.length === 1 ? '1 Buchung' : `${String(zeilen.length)} Buchungen`}
          {mehr ? ' — es gibt weitere' : ''}
        </p>
      )}
      {zeilen !== null && zeilen.length === 0 && (
        <p className="liste__leer">
          {gefiltert ? 'Keine Buchung zu diesen Filtern.' : 'Keine Buchung mit Termin ab heute.'}
        </p>
      )}
      {zeilen !== null && zeilen.length > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Buchungen" ref={tabelle}>
            <thead>
              <tr>
                <th scope="col">Buchung</th>
                <th scope="col">Kunde</th>
                <th scope="col">Training und Termin</th>
                <th scope="col">Plätze</th>
                <th scope="col">Stand</th>
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z) => (
                <tr key={z.id}>
                  <td>
                    <Maskenverweis
                      className="liste__nummer"
                      maske={{ art: 'buchungsvorgang', nummer: z.buchungsnummer }}
                      beiKlick={() => {
                        zuletztGeoeffnet = z.buchungsnummer;
                        beiOeffnen(z.buchungsnummer);
                      }}
                    >
                      {z.buchungsnummer}
                    </Maskenverweis>
                    {z.auftragsnummer !== null && (
                      <span className="kanalliste__name"> · {z.auftragsnummer}</span>
                    )}
                  </td>
                  <td>
                    {z.kundenname}
                    <span className="kanalliste__name"> · {z.kundennummer}</span>
                  </td>
                  <td>
                    {z.produkt}
                    <br />
                    <span className="kanalliste__name liste__einzeilig">{zeitpunkt.format(new Date(z.beginn))}</span>
                  </td>
                  <td>{z.plaetze}</td>
                  <td>{STANDNAME[z.status] ?? z.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mehr && (
        <div className="knopfreihe">
          <button type="button" className="knopf" disabled={laeuft} onClick={() => void weitere()}>
            Weitere laden
          </button>
        </div>
      )}
    </section>
  );
}
